<template>
    <div class="relative rounded flex">
        <div
            v-if="highlight"
            class="bg-secondary absolute left-1/2 -translate-x-1/2 -top-8 py-4 px-16 text-white rounded z-40"
        >
            <p class="text-body-sm font-semibold text-white">{{ highlight }}</p>
        </div>
        <div
            class="shadow w-316 bg-white p-24 border rounded shrink-0 flex flex-1 flex-col justify-between"
            :class="highlight ? 'border-green-dark border-2 z-0' : 'border-gray-100'"
        >
            <div class="h-full flex flex-col">
                <!-- card image -->
                <div class="mb-12 rounded overflow-hidden w-[270px] h-[274px]" :style="{ backgroundColor: '#F5F5F8' }">
                    <div
                        :style="{ backgroundImage: 'url(' + card.image_url + ')' }"
                        class="w-full h-full bg-cover inset-0 bg-no-repeat bg-center max-h-232"
                    />
                    <ul id="card-tags" class="flex flex-row gap-12 pl-12 overflow-scroll hide-scrollbar">
                        <tag>
                            {{ card.price || card.has_subscription ? $t('general.premium') : $t('general.free') }}
                        </tag>
                        <tag
                            v-if="hasCustomTags && customTag"
                            :display="customTag.color"
                            :icon="customTag.icon"
                        >
                            {{ capitalize(customTag.label) }}
                        </tag>
                        <tag v-for="(tag, index) in slicedTags" :key="index">
                            {{ capitalize(tag) }}
                        </tag>
                    </ul>
                </div>
                <!-- card details -->
                <div class="grow space-y-12 flex flex-col justify-between">
                    <div>
                        <div class="text-left flex items-center gap-4">
                            <p class="text-body font-semibold text-black">
                                {{ title() }}
                            </p>

                            <div
                                class="cursor-pointer"
                                v-if="showFavoriteButton"
                                @click="toggleFavorite"
                                @mouseover="hoverFilled = true"
                                @mouseleave="hoverFilled = false"
                            >
                                <svg-icon :type="svgType" class="text-orange" size="24"/>
                            </div>
                        </div>

                        <div v-if="price" class="flex items-center mb-8">
                            <p v-if="customPrice" class="flex text-secondary text-body font-semibold mr-4">
                                <Currency class="font-semibold"/>{{ customPrice }}
                            </p>
                            <p
                                class="flex"
                                :class="customPrice ? 'text-primary-80 text-body-sm line-through' : 'text-body font-semibold'"
                            >
                                <Currency class="font-semibold"/>{{ price }}
                            </p>
                        </div>

                        <p class="text-body-sm text-black" v-html="description()"></p>
                    </div>

                    <simple-button
                        class="w-full"
                        :class="isButtonDisabled ? 'cursor-not-allowed' : 'cursor-pointer'"
                        :disabled="isButtonDisabled"
                        display="secondary"
                        look="primary"
                        @click="handleClick"
                    >
                        {{ buttonLabel() }}
                    </simple-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Currency from './Currency.vue';
import ShareJobCard from '../models/ShareJobCard';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import { storeFavorites, deleteFavorites } from '../services/ShareJobService';
import ShareJobCardFavorite from '../models/ShareJobCardFavorite';
import Job from '../models/Job';
import SimpleButton from "./ui/SimpleButton.vue";
import SvgIcon from "./ui/SvgIcon.vue";
import capitalize from "../mixins/capitalize";
import PaidSubscription from "../models/PaidSubscription";
import {ADD_NOTIFICATION} from "../state/mutation-types";
import Tag from "./ui/Tag.vue";

export default {
    components: {Tag, SimpleButton, SvgIcon, Currency},
    data() {
        return {
            error: null,
            hoverFilled: false,
            userFavorites: [],
        }
    },
    props: {
        cardId: Number,
        job: Job,
        showFavoriteButton: Boolean,
        activeLocale: String,
    },
    mixins: [capitalize],
    computed: {
        ...mapState(useMainStore, ['currentUser', 'currentCompany', 'selectedLocale', 'locale']),
        card() {
            return useRepo(ShareJobCard).find(this.cardId);
        },
        slicedTags() {
            return this.tags ? this.tags.slice(0, 2) : [];
        },
        locale() {
            return this.selectedLocale;
        },
        price() {
            return this.card.price;
        },
        customPrice() {
            return this.card.custom_price;
        },
        tags() {
            return this.card.tags;
        },
        highlight() {
            const isPublished = this.job.is_published;
            let label = isPublished ? this.card.highlight : this.card.unpublished_highlight

            return this.findLabelByLocale(label, isPublished);
        },
        type() {
            return this.card.type;
        },
        svgType() {
            return !this.hoverFilled && !this.card.isFavorited ? 'star' : 'star-filled';
        },
        isValidSubscriptionState() {
            if (this.card.has_subscription) {
                if (this.card.subscription_type === 'vdab') {
                    const status = this.currentCompany.vdab_status;
                    return  status === 'live' || status === 'test' || status === null;
                } else if (this.card.subscription_type === 'multi-posting') {
                    const status = this.currentCompany.multiposting_status;
                    return status === 'active' || status === 'inactive';
                }
            }

            return true;
        },
        isButtonDisabled() {
            let isDisabled = false;
            if (this.card.extra?.ignoreUnpublished) return isDisabled;

            if (this.card.has_subscription) {
                isDisabled = !this.isValidSubscriptionState;
            } else {
                isDisabled = !this.job.is_published
                    || !this.card.type;
            }

            if (this.card.subscription_type === 'vdab' && !this.job.is_published ) {
                // should not be disabled if vdab_status === test
                isDisabled = this.currentCompany.vdab_status !== 'test'
            }

            return isDisabled;
        },
        paidSubscription() {
            return useRepo(PaidSubscription)
                .where('company_id', this.currentCompany.id)
                .where('type', this.card.subscription_type)
                .where('active', true)
                .first();
        },
        hasCustomTags() {
            return this.card.has_subscription
                || this.card.type_ref === 'google_for_jobs'
                || this.card.type_ref === 'monster_job'
                || this.card.type_ref === 'career_jet';
        },
        customTag() {
            switch (this.card.type_ref) {
                case 'vdab':
                case 'multi-posting':
                    return this.subscriptionTag;
                case 'google_for_jobs':
                    return this.googleForJobsTag;
                case 'monster_job':
                case 'career_jet':
                    return this.monsterOrCareerJetTag;
                default:
                    return;
            }
        },
        subscriptionTag() {
            if (this.card.subscription_type === 'vdab') {
                switch (this.currentCompany.vdab_status) {
                    case 'live':
                        return {
                            color: 'secondary',
                            icon: 'check-circle',
                            label: this.$t('general.active'),
                        };
                    case 'pending':
                        return {
                            color: 'warning',
                            icon: 'change-circle',
                            label: this.$t('general.pending'),
                        };
                    case 'test':
                        return {
                            color: 'primary',
                            icon: 'settings',
                            label: this.$t('general.testing'),
                        };
                    default:
                        return;
                }
            } else if (this.card.subscription_type === 'multi-posting') {
                switch (this.currentCompany.multiposting_status) {
                    case 'active':
                        return {
                            color: 'secondary',
                            icon: 'check-circle',
                            label: this.$t('general.active'),
                        };
                    case 'pending':
                        return {
                            color: 'warning',
                            icon: 'change-circle',
                            label: this.$t('general.pending'),
                        };
                    default:
                        return;
                }
            }
        },
        googleForJobsTag() {
            const job = this.job;
            if (!job.company_location_id && (!job.postal_code || !job.city || !job.country)) {
                return {
                    color: 'warning',
                    icon: 'warning',
                    label: this.$t('general.info_needed'),
                };
            } else {
                return {
                    color: 'secondary',
                    icon: 'check-circle',
                    label: this.$t('general.active'),
                };
            }
        },
        monsterOrCareerJetTag() {
            if (this.job.is_published) {
                return {
                    color: 'secondary',
                    icon: 'check-circle',
                    label: this.$t('general.active'),
                };
            } else {
                return false;
            }
        },
        jobsiteLocale() {
            if (this.activeLocale && this.job.active[this.activeLocale]) {
                return this.activeLocale;
            }

            if (this.job.active[this.locale]) {
                return this.locale;
            }

            // select first active locale
            for (const [key, value] of Object.entries(this.job.active)) {
                if (value) {
                    return key;
                }
            }

            // force locale
            return this.locale;
        },
        jobsite_url() {
            return this.job.url[this.jobsiteLocale];
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        addToFavorites() {
            storeFavorites(this.cardId)
                .then(data => {
                    return useRepo(ShareJobCardFavorite)
                        .save({ user_id: this.currentUser.id, share_job_card_id: data.data });
                })
        },
        removeFavorite() {
            deleteFavorites(this.cardId)
                .then(data => {
                    useRepo(ShareJobCardFavorite)
                        .where('user_id', this.currentUser.id)
                        .where('share_job_card_id', data.data)
                        .delete();
                });
        },
        toggleFavorite() {
            // reset hover state
            this.hoverFilled = false;
            if (this.card.isFavorited) {
                this.removeFavorite();
            } else {
                this.addToFavorites();
            }
        },
        title(forcePublished = false) {
            const isPublished = this.job.is_published;
            let label = forcePublished || isPublished
                ? this.card.title
                : this.card.unpublished_title;

            const value = this.findLabelByLocale(label, isPublished, forcePublished);
            if (value || (forcePublished && value === '')) return value;

            if (!isPublished) {
                return this.title(true)
            }
        },
        description(forcePublished = false) {
            const isPublished = this.job.is_published;
            let label = forcePublished || isPublished
                ? this.card.description
                : this.card.unpublished_description;

            const value = this.findLabelByLocale(label, isPublished, forcePublished);
            if (value || (forcePublished && value === '')) return value;

            if (!isPublished) {
                return this.description(true);
            }
        },
        buttonLabel(forcePublished = false) {
            // overwrite button label if request is needed
            if (this.card.has_subscription && this.card.subscription_type && !this.paidSubscription
                // for vdab do not show activate if status is on test or live
                && this.currentCompany.vdab_status !== 'test' && this.currentCompany.vdab_status !== 'live' ) {
                return this.getRequestSubscriptionLabel();
            }

            const isPublished = this.job.is_published;
            let label = forcePublished || isPublished
                ? this.card.button_label
                : this.card.unpublished_button_label;

            if (this.card.subscription_type === 'vdab') {
                label = this.currentCompany.vdab_status !== 'test' &&  this.currentCompany.vdab_status !== 'live' ?
                    this.card.unpublished_button_label : this.card.button_label;
            }

            const value = this.findLabelByLocale(label, isPublished, forcePublished);
            if (value || (forcePublished && value === '')) return value;

            if (!isPublished) {
                return this.buttonLabel(true);
            }
        },
        findLabelByLocale(label, isPublished, forcePublished = false) {
            if (label[this.locale]) {
                return label[this.locale];
            } else if (label['en']) {
                return label['en'];
            } else if (forcePublished || isPublished) {
                for (const [key, value] of Object.entries(label)) {
                    if (value) {
                        return value;
                    }
                }
            }

            return '';
        },
        getRequestSubscriptionLabel() {
            if (this.card.subscription_type === 'vdab') {
                return this.$t('share_job.subscription.request.vdab');
            } else if (this.card.subscription_type === 'multi-posting') {
                return this.$t('share_job.subscription.request.multiposting');
            } else {
                return this.$t('general.request');
            }
        },
        handleClick() {
            if (this.card.type === 'popup') {
                this.$emit('selectedCard', this.card);
            } else if (this.card.type_ref === 'vacancy_link' || this.card.type_ref === 'application_form') {
                let copyToClipboard = this.job.url[this.activeLocale];
                if (this.card.type_ref === 'application_form') {
                    copyToClipboard += `/${this.$t('routing.apply', this.activeLocale)}`;
                }
                // copy job url to clipboard
                navigator.clipboard.writeText(copyToClipboard)
                    .then(() => this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('general.copied_to_clipboard', {subject: this.title().toLowerCase()}).toString(),
                    }))
                    .catch(() => this[ADD_NOTIFICATION]({
                        type: 'error',
                        message: this.$t('general.error'),
                    }));
            } else if (this.card.has_subscription && this.card.subscription_type && !this.paidSubscription) {
                this.$emit('selectedCard', this.card);
            } else if (this.card.type === 'redirect') {
                if (this.card.type_ref === 'campaigns') {
                    this.card.type_ref = 'multi-posting';
                }

                const features = this.card.type_ref === 'socials'
                    ? 'height=350,width=580,resizable=0,toolbar=0,menubar=0,status=0,location=0,scrollbars=0'
                    : '';
                let uri = this.card.extra?.uri;

                if (this.card.type_ref === 'socials') {
                    switch (this.card.extra?.medium) {
                        case 'Facebook':
                            uri = `http://www.facebook.com/sharer.php?u=${this.jobsite_url}`;
                            break;
                        case 'Twitter':
                            uri = `https://twitter.com/share?url=${this.jobsite_url}`;
                            break;
                        case 'Linkedin':
                            uri = `https://www.linkedin.com/shareArticle?mini=true&url=${this.jobsite_url}`;
                            break;
                        case 'Whatsapp':
                            uri = `https://web.whatsapp.com/send?text=${this.jobsite_url}`;
                            break;
                    }
                }

                if (uri) {
                    window.open(uri, this.card.extra?.medium, features)
                } else {
                    this.$router.push({ name: this.card.type_ref, params: {
                            ...this.card.extra?.meta,
                            jobId: this.job.id,
                        }
                    });
                }
            }
        }
    },
    beforeMount() {
        if (this.card.has_subscription && (!this.paidSubscription || this.card.type_ref === 'campaigns')) {
            if (!useRepo(PaidSubscription).first()) {
                PaidSubscription.fetch();
            }
        }
    },
}
</script>
